import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Compass, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inrFull } from "@/lib/format";
import { CompassQuestions, COMPASS_COLORS as G } from "@/components/compass/CompassQuestions";
import { CompassResults, openPdf } from "@/components/compass/CompassResults";

const show = (v) => v || <span className="text-amber-700">Not recorded</span>;

function RecordCheck({ ctx, age, setAge, consent, setConsent, onStart }) {
  const p = ctx.pet;
  return (
    <div className="space-y-3 text-sm" data-testid="compass-staff-details">
      <p className="text-slate-600">Confirm these details from the pet's record with the parent. To change the record, use <b>Edit pet</b> — Pet Compass never edits it.</p>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3">
        <span>Species: {show(p.species)}</span><span>Breed: {show(p.breed)}</span>
        <span>Sex: {show(p.gender)}</span><span>Spay/neuter: {show({ yes: "Yes", no: "No" }[p.neuter_status])}</span>
        <span className="col-span-2">Age: {p.age_years != null ? `${p.age_years} years` : <>Not recorded — <input type="number" min="0" max="40" value={age} onChange={(e) => setAge(e.target.value)} placeholder="age (optional)" className="ml-1 w-28 rounded border px-2 py-1" data-testid="compass-staff-age" /></>}</span>
      </div>
      <label className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="compass-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="compass-consent-checkbox" />
        <span>The pet parent agrees that their answers are saved to {p.name || "this pet"}'s record at this clinic.</span>
      </label>
      <Button onClick={onStart} disabled={!consent} data-testid="compass-staff-start" className="bg-slate-900 text-white">Start the 7 questions</Button>
    </div>
  );
}

function VaxContext({ vax }) {
  return (
    <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs" data-testid="compass-vax-records">
      <p className="font-bold text-slate-700">Vaccination records on file — ask the parent to confirm they're current</p>
      {vax.length ? <ul className="mt-1 list-disc pl-4">{vax.slice(0, 8).map((v, i) => <li key={i}>{v.vaccine || "Vaccine"} · given {v.administered_date || "date not recorded"} · next due {v.next_due_date || "not recorded"}</li>)}</ul>
        : <p className="mt-1 text-slate-500">No vaccination records on file.</p>}
    </div>
  );
}

function BookingCta({ r, ctx, onBook }) {
  if (r.urgent?.show) return <p className="rounded-2xl bg-rose-50 p-3 text-sm text-rose-800" data-testid="compass-staff-urgent-note">Urgent concerns reported — advise the parent to see a veterinarian promptly. No service suggestions are shown.</p>;
  return (
    <div className="rounded-3xl p-4 text-sm" style={{ background: G.mint }} data-testid="compass-booking-cta">
      <p className="font-extrabold" style={{ color: G.deep }}>Optional: book a routine health check</p>
      {ctx.vet_services.length ? <ul className="mt-1 text-slate-700">{ctx.vet_services.slice(0, 5).map((s) => <li key={s.id}>{s.name} · {inrFull(s.price)}</li>)}</ul>
        : <p className="mt-1 text-slate-600">No veterinary services are set up with a price at this business. Advise the parent to contact their veterinarian.</p>}
      {ctx.vet_services.length > 0 && <Button size="sm" onClick={onBook} className="mt-2 bg-slate-900 text-white" data-testid="compass-book-btn">Open booking form</Button>}
      <p className="mt-1 text-xs text-slate-500">Opens the normal booking form. Choose the service and confirm it with the parent; nothing is booked or charged automatically.</p>
    </div>
  );
}

export function PetCompassPanel({ petId, canSave }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("home");
  const [answers, setAnswers] = useState({});
  const [age, setAge] = useState("");
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["pet-compass", petId], enabled: open, queryFn: async () => (await api.get(`/pets/${petId}/compass`)).data });
  const ctx = q.data;
  const pdf = (id, print) => openPdf(api.get(`/pet-compass/${id}/pdf`, { responseType: "blob" }), `pet-compass-${ctx?.pet?.name || "report"}.pdf`, print).catch((e) => toast.error(apiErr(e)));
  const view_ = async (id) => { setResult((await api.get(`/pet-compass/${id}`)).data); setView("results"); };
  const save = async () => {
    setBusy(true);
    try {
      const d = (await api.post(`/pets/${petId}/compass`, { answers: Object.fromEntries(Object.entries(answers).filter(([, v]) => v)), consent_to_save: consent, age_years: age === "" ? null : Number(age) })).data;
      setResult(d); setView("results"); qc.invalidateQueries({ queryKey: ["pet-compass", petId] }); toast.success("Assessment saved to the pet's record");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const reset = () => { setAnswers({}); setConsent(false); setResult(null); setView("details"); };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setOpen(true); setView("home"); }} data-testid="pet-compass-btn" className="w-full gap-1 min-h-[44px]"><Compass className="h-4 w-4" /> Pet Compass</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-white" data-testid="pet-compass-dialog">
          <DialogHeader><DialogTitle>Pet Compass {ctx?.pet?.name ? `· ${ctx.pet.name}` : ""}</DialogTitle></DialogHeader>
          {!ctx ? <p className="text-sm text-slate-400">Loading…</p> : <>
            {view === "home" && (
              <div className="space-y-3" data-testid="compass-history">
                {canSave && <Button onClick={reset} data-testid="compass-new-btn" className="bg-slate-900 text-white">New assessment</Button>}
                {ctx.history.length ? ctx.history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3 text-sm" data-testid={`compass-history-${h.id}`}>
                    <span>{h.assessed_on} · v{h.version} · by {h.created_by_name || "staff"}</span>
                    <span className="flex gap-3"><button className="font-semibold underline" onClick={() => view_(h.id)} data-testid={`compass-view-${h.id}`}>View</button><button className="font-semibold underline" onClick={() => pdf(h.id)}><FileDown className="inline h-3.5 w-3.5" /> PDF</button></span>
                  </div>)) : <p className="text-sm text-slate-500">No assessments yet.</p>}
              </div>)}
            {view === "details" && <RecordCheck ctx={ctx} age={age} setAge={setAge} consent={consent} setConsent={setConsent} onStart={() => setView("questions")} />}
            {view === "questions" && <CompassQuestions questions={ctx.questions} answers={answers} setAnswers={setAnswers} onDone={save} onBack={() => setView("details")} busy={busy} context={{ preventive: <VaxContext vax={ctx.vaccinations} /> }} />}
            {view === "results" && result && (
              <CompassResults r={result.results} onPdf={() => pdf(result.id)} onPrint={() => pdf(result.id, true)} onRetake={canSave ? reset : null}>
                <BookingCta r={result.results} ctx={ctx} onBook={() => { setOpen(false); navigate(`/bookings?new=1&customer_id=${ctx.customer_id}`); }} />
              </CompassResults>)}
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
